import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconArrowLeft,
  IconCancel,
  IconChecks,
  IconMail,
  IconPlayerPause,
  IconPlayerPlay,
  IconRefresh,
  IconSend,
  IconShield,
} from '@tabler/icons-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAdminView } from '../contexts/AdminViewContext';
import './AdminNewsletterPage.css';

const DEFAULT_HTML = `<!doctype html>
<html>
  <body style="margin:0;background:#f7f4ef;font-family:Arial,sans-serif;color:#23211f;">
    <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
      <tr>
        <td align="center" style="padding:32px 16px;">
          <table width="640" cellpadding="0" cellspacing="0" role="presentation" style="max-width:640px;background:#ffffff;border-radius:16px;overflow:hidden;">
            <tr>
              <td style="padding:32px;">
                <h1 style="margin:0 0 16px;font-size:28px;line-height:1.2;">Mythic Maps update</h1>
                <p style="margin:0 0 18px;font-size:16px;line-height:1.6;">
                  Replace this with the newsletter content.
                </p>
                <a href="https://mythicmaps.ai/create" style="display:inline-block;background:#fc5200;color:white;text-decoration:none;border-radius:10px;padding:12px 18px;font-weight:700;">
                  Create a map
                </a>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;

const audienceOptions = [
  {
    value: 'admins',
    label: 'Admins only',
    helper: 'Send to users in the admins collection.'
  },
  {
    value: 'specified_emails',
    label: 'Specified addresses',
    helper: 'Paste email addresses separated by commas, spaces, or new lines.'
  },
  {
    value: 'user_ids',
    label: 'Specified user IDs',
    helper: 'Paste Firebase user IDs and use their stored email addresses.'
  },
  {
    value: 'all_users',
    label: 'All users with email',
    helper: 'Send to every non-deleted user with a valid email address, plus all newsletter signups (promo email captures). Duplicates are removed.'
  }
];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatPercent = (job) => {
  const total = Number(job?.total) || 0;
  if (!total) return 0;
  return Math.min(100, Math.round(((Number(job?.sent) || 0) / total) * 100));
};

function AdminNewsletterPage({ user }) {
  const navigate = useNavigate();
  const { isAdmin } = useAdminView();
  const shouldContinueRef = useRef(false);

  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [text, setText] = useState('');
  const [mode, setMode] = useState('admins');
  const [emailsInput, setEmailsInput] = useState('');
  const [userIdsInput, setUserIdsInput] = useState('');
  const [limit, setLimit] = useState('');
  const [batchSize, setBatchSize] = useState(100);
  const [includeUnsubscribed, setIncludeUnsubscribed] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [prioritizeAdmins, setPrioritizeAdmins] = useState(true);
  const [preview, setPreview] = useState(null);
  const [job, setJob] = useState(null);
  const [jobIdInput, setJobIdInput] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const selection = useMemo(() => ({
    mode,
    emails: emailsInput,
    userIds: userIdsInput,
    includeUnsubscribed,
    includeDeleted,
    prioritizeAdmins,
    limit,
  }), [
    mode,
    emailsInput,
    userIdsInput,
    includeUnsubscribed,
    includeDeleted,
    prioritizeAdmins,
    limit,
  ]);

  const previewNewsletterRecipients = httpsCallable(functions, 'previewNewsletterRecipients');
  const startNewsletterSendJob = httpsCallable(functions, 'startNewsletterSendJob');
  const processNewsletterSendBatch = httpsCallable(functions, 'processNewsletterSendBatch');
  const getNewsletterSendJob = httpsCallable(functions, 'getNewsletterSendJob');
  const cancelNewsletterSendJob = httpsCallable(functions, 'cancelNewsletterSendJob');

  const handlePreviewRecipients = async () => {
    setIsPreviewing(true);
    setStatusMessage('');
    try {
      const result = await previewNewsletterRecipients({ selection });
      setPreview(result.data);
      setStatusMessage(`Preview found ${result.data?.recipients?.length || 0} eligible recipients.`);
    } catch (error) {
      console.error('Error previewing newsletter recipients:', error);
      setStatusMessage(error.message || 'Failed to preview recipients.');
    } finally {
      setIsPreviewing(false);
    }
  };

  const refreshJob = async (jobId) => {
    const result = await getNewsletterSendJob({ jobId });
    setJob(result.data);
    setJobIdInput(result.data.jobId);
    return result.data;
  };

  const runJobLoop = async (jobId) => {
    shouldContinueRef.current = true;
    setIsProcessing(true);
    setStatusMessage('Sending newsletter batches...');
    try {
      let latest = await refreshJob(jobId);
      while (
        shouldContinueRef.current &&
        !['completed', 'cancelled'].includes(latest.status)
      ) {
        if (latest.status === 'failed') {
          setStatusMessage(`Send paused after an error: ${latest.lastError || 'Unknown error'}`);
          break;
        }
        const result = await processNewsletterSendBatch({ jobId });
        latest = result.data;
        setJob(latest);
        if (latest.alreadyRunning) {
          await wait(1500);
        } else {
          // Keep well clear of Resend's account-wide requests/second limit:
          // with fast callable roundtrips a 300ms gap let several batch
          // requests land in the same one-second window (observed 429s).
          await wait(1200);
        }
      }
      if (latest.status === 'completed') {
        setStatusMessage(`Newsletter send complete: ${latest.sent} of ${latest.total} sent.`);
      } else if (latest.status === 'cancelled') {
        setStatusMessage('Newsletter send cancelled.');
      } else if (!shouldContinueRef.current) {
        setStatusMessage('Newsletter send paused after the current batch.');
      }
    } catch (error) {
      console.error('Error processing newsletter job:', error);
      setStatusMessage(error.message || 'Failed while sending newsletter.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStartSending = async () => {
    setIsStarting(true);
    setStatusMessage('');
    try {
      const result = await startNewsletterSendJob({
        subject,
        html,
        text,
        batchSize,
        selection,
      });
      setJob(result.data);
      setJobIdInput(result.data.jobId);
      setStatusMessage(`Created newsletter job for ${result.data.total} recipients.`);
      await runJobLoop(result.data.jobId);
    } catch (error) {
      console.error('Error starting newsletter job:', error);
      setStatusMessage(error.message || 'Failed to start newsletter send.');
    } finally {
      setIsStarting(false);
    }
  };

  const handlePause = () => {
    shouldContinueRef.current = false;
  };

  const handleCancel = async () => {
    if (!job?.jobId) return;
    setIsCancelling(true);
    try {
      const result = await cancelNewsletterSendJob({ jobId: job.jobId });
      shouldContinueRef.current = false;
      setJob(result.data);
      setStatusMessage('Newsletter job cancelled.');
    } catch (error) {
      console.error('Error cancelling newsletter job:', error);
      setStatusMessage(error.message || 'Failed to cancel newsletter job.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleLoadJob = async () => {
    if (!jobIdInput.trim()) return;
    setStatusMessage('');
    try {
      const loaded = await refreshJob(jobIdInput.trim());
      setStatusMessage(`Loaded newsletter job ${loaded.jobId}.`);
    } catch (error) {
      console.error('Error loading newsletter job:', error);
      setStatusMessage(error.message || 'Failed to load newsletter job.');
    }
  };

  const recipientEmails = preview?.recipientEmails || [];
  const progressPercent = formatPercent(job);
  const canStart = subject.trim() && html.trim() && !isStarting && !isProcessing;
  const canResume = job?.jobId && ['ready', 'failed', 'sending'].includes(job.status) && !isProcessing;

  if (!user || !isAdmin) {
    return (
      <div className="admin-newsletter-page">
        <div className="newsletter-not-authorized">
          <IconShield size={64} />
          <h2>Admin Access Required</h2>
          <p>You need administrator privileges to access this area.</p>
          <button onClick={() => user?.uid ? navigate(`/profile/${user.uid}`) : navigate('/')}>Back to Profile</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-newsletter-page">
      <div className="admin-newsletter-container">
        <header className="newsletter-header">
          <button className="newsletter-back-button" onClick={() => navigate(-1)}>
            <IconArrowLeft size={20} />
            Back
          </button>
          <div className="newsletter-title-row">
            <div className="newsletter-title-icon">
              <IconMail size={34} />
            </div>
            <div>
              <h1>Newsletter Sending</h1>
              <p>Prepare, preview, and batch-send HTML newsletters through Resend.</p>
            </div>
          </div>
        </header>

        <div className="newsletter-layout">
          <section className="newsletter-panel newsletter-compose-panel">
            <div className="panel-heading">
              <h2>Message</h2>
              <span>HTML template</span>
            </div>
            <label className="newsletter-field">
              <span>Subject</span>
              <input
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="June update from Mythic Maps"
              />
            </label>
            <label className="newsletter-field">
              <span>HTML email template</span>
              <textarea
                className="newsletter-html-input"
                value={html}
                onChange={(event) => setHtml(event.target.value)}
                spellCheck={false}
              />
            </label>
            <label className="newsletter-field">
              <span>Plain text fallback</span>
              <textarea
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="Optional. If empty, the backend derives this from the HTML."
              />
            </label>
          </section>

          <section className="newsletter-panel">
            <div className="panel-heading">
              <h2>Audience</h2>
              <span>Recipient controls</span>
            </div>
            <div className="audience-options">
              {audienceOptions.map((option) => (
                <label
                  key={option.value}
                  className={`audience-option${mode === option.value ? ' active' : ''}`}
                >
                  <input
                    type="radio"
                    checked={mode === option.value}
                    onChange={() => setMode(option.value)}
                  />
                  <strong>{option.label}</strong>
                  <span>{option.helper}</span>
                </label>
              ))}
            </div>

            {mode === 'specified_emails' && (
              <label className="newsletter-field">
                <span>Email addresses</span>
                <textarea
                  value={emailsInput}
                  onChange={(event) => setEmailsInput(event.target.value)}
                  placeholder="person@example.com, another@example.com"
                />
              </label>
            )}

            {mode === 'user_ids' && (
              <label className="newsletter-field">
                <span>User IDs</span>
                <textarea
                  value={userIdsInput}
                  onChange={(event) => setUserIdsInput(event.target.value)}
                  placeholder="Firebase user IDs separated by new lines"
                />
              </label>
            )}

            <div className="newsletter-grid-controls">
              <label className="newsletter-field">
                <span>Recipient limit</span>
                <input
                  type="number"
                  min="1"
                  value={limit}
                  onChange={(event) => setLimit(event.target.value)}
                  placeholder="No limit"
                />
              </label>
              <label className="newsletter-field">
                <span>Batch size</span>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={batchSize}
                  onChange={(event) => setBatchSize(event.target.value)}
                />
              </label>
            </div>

            <div className="newsletter-checkboxes">
              <label>
                <input
                  type="checkbox"
                  checked={prioritizeAdmins}
                  onChange={(event) => setPrioritizeAdmins(event.target.checked)}
                />
                Prioritize admins when a limit is set
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={includeUnsubscribed}
                  onChange={(event) => setIncludeUnsubscribed(event.target.checked)}
                />
                Include globally unsubscribed emails
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(event) => setIncludeDeleted(event.target.checked)}
                />
                Include deleted accounts
              </label>
            </div>

            <div className="newsletter-actions-row">
              <button
                className="newsletter-secondary-button"
                onClick={handlePreviewRecipients}
                disabled={isPreviewing}
              >
                <IconChecks size={18} />
                {isPreviewing ? 'Previewing...' : 'Preview Recipients'}
              </button>
              <button
                className="newsletter-primary-button"
                onClick={handleStartSending}
                disabled={!canStart}
              >
                <IconSend size={18} />
                {isStarting ? 'Starting...' : 'Start Sending'}
              </button>
            </div>
          </section>

          <section className="newsletter-panel newsletter-preview-panel">
            <div className="panel-heading">
              <h2>Email Preview</h2>
              <span>{subject || 'No subject yet'}</span>
            </div>
            <iframe title="Newsletter preview" srcDoc={html} />
          </section>

          <section className="newsletter-panel">
            <div className="panel-heading">
              <h2>Recipient Preview</h2>
              <span>{recipientEmails.length} eligible</span>
            </div>
            {preview ? (
              <>
                <div className="newsletter-summary-strip">
                  <div>
                    <strong>{preview.recipients?.length || 0}</strong>
                    <span>eligible</span>
                  </div>
                  <div>
                    <strong>{preview.totalCandidates || 0}</strong>
                    <span>candidates</span>
                  </div>
                  <div>
                    <strong>{Object.values(preview.skipped || {}).reduce((sum, value) => sum + value, 0)}</strong>
                    <span>skipped</span>
                  </div>
                </div>
                <textarea
                  className="recipient-list"
                  value={recipientEmails.join('\n')}
                  readOnly
                />
                {preview.skipped && Object.keys(preview.skipped).length > 0 && (
                  <div className="skipped-list">
                    {Object.entries(preview.skipped).map(([reason, count]) => (
                      <span key={reason}>{reason}: {count}</span>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="empty-state">Preview recipients before sending.</p>
            )}
          </section>

          <section className="newsletter-panel newsletter-job-panel">
            <div className="panel-heading">
              <h2>Send Progress</h2>
              <span>{job?.status || 'No active job'}</span>
            </div>
            <div className="job-loader">
              <input
                value={jobIdInput}
                onChange={(event) => setJobIdInput(event.target.value)}
                placeholder="Newsletter job ID"
              />
              <button className="newsletter-secondary-button" onClick={handleLoadJob}>
                <IconRefresh size={18} />
                Load
              </button>
            </div>
            <div className="progress-bar-shell">
              <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }} />
            </div>
            <div className="newsletter-summary-strip">
              <div>
                <strong>{job?.sent || 0}</strong>
                <span>sent</span>
              </div>
              <div>
                <strong>{job?.total || 0}</strong>
                <span>total</span>
              </div>
              <div>
                <strong>{progressPercent}%</strong>
                <span>progress</span>
              </div>
            </div>
            {job?.lastError && (
              <div className="newsletter-error">{job.lastError}</div>
            )}
            <div className="newsletter-actions-row">
              <button
                className="newsletter-secondary-button"
                disabled={!canResume}
                onClick={() => runJobLoop(job.jobId)}
              >
                <IconPlayerPlay size={18} />
                Continue
              </button>
              <button
                className="newsletter-secondary-button"
                disabled={!isProcessing}
                onClick={handlePause}
              >
                <IconPlayerPause size={18} />
                Pause
              </button>
              <button
                className="newsletter-danger-button"
                disabled={!job?.jobId || isCancelling || job.status === 'completed'}
                onClick={handleCancel}
              >
                <IconCancel size={18} />
                Cancel
              </button>
            </div>
            {statusMessage && <div className="newsletter-status">{statusMessage}</div>}
          </section>
        </div>
      </div>
    </div>
  );
}

export default AdminNewsletterPage;
